import { Injectable, signal } from '@angular/core';
import { Theme } from '../interfaces/types/theme.type';

const MODUS_THEME_CONFIG_KEY = 'modus-theme-config';
const LEGACY_THEME_KEY = 'modus-theme';

export interface ModusThemeConfig {
  mode: Theme;
  theme?: string;
}

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly _currentTheme = signal<Theme>(this.readModeFromDocument());
  private mutationObserver?: MutationObserver;

  currentTheme = this._currentTheme.asReadonly();

  constructor() {
    this.bridgeBootstrapTheme(this._currentTheme());
    this.observeModusTheme();
  }

  syncFromModusChange(config: ModusThemeConfig): void {
    if (config.mode === 'light' || config.mode === 'dark') {
      this._currentTheme.set(config.mode);
      this.bridgeBootstrapTheme(config.mode);
    }
  }

  private observeModusTheme(): void {
    this.mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type !== 'attributes') {
          continue;
        }
        const attr = mutation.attributeName;
        if (attr !== 'data-mode' && attr !== 'data-theme') {
          continue;
        }
        const mode = this.readModeFromDocument();
        if (mode !== this._currentTheme()) {
          this._currentTheme.set(mode);
          this.bridgeBootstrapTheme(mode);
        }
      }
    });

    this.mutationObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-mode', 'data-theme'],
    });
  }

  private readModeFromDocument(): Theme {
    const dataMode = document.documentElement.getAttribute('data-mode');
    if (dataMode === 'light' || dataMode === 'dark') {
      return dataMode;
    }

    const dataTheme = document.documentElement.getAttribute('data-theme');
    if (dataTheme?.endsWith('-dark')) {
      return 'dark';
    }
    if (dataTheme?.endsWith('-light')) {
      return 'light';
    }

    const storedMode = this.readStoredMode();
    if (storedMode) {
      return storedMode;
    }

    if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }

    return 'light';
  }

  private readStoredMode(): Theme | null {
    try {
      const stored = localStorage.getItem(MODUS_THEME_CONFIG_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as { mode?: string };
        if (parsed.mode === 'light' || parsed.mode === 'dark') {
          return parsed.mode;
        }
      }
    } catch {
      // ignore malformed config
    }

    const legacy = localStorage.getItem(LEGACY_THEME_KEY);
    if (legacy === 'light' || legacy === 'dark') {
      return legacy;
    }

    return null;
  }

  private bridgeBootstrapTheme(theme: Theme): void {
    const html = document.documentElement;
    html.setAttribute('data-bs-theme', theme);
    if (theme === 'dark') {
      html.classList.add('dark-theme');
    } else {
      html.classList.remove('dark-theme');
    }
  }

  ngOnDestroy(): void {
    this.mutationObserver?.disconnect();
  }
}
